"use client";

import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslations } from "next-intl";
import { getLocalizedField, type PinnedAnnouncementPublic } from "@/features/courses/api";
import type { Locale } from "@/lib/i18n";

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function pinnedBodyPlain(
  pinned: PinnedAnnouncementPublic,
  locale: Locale,
): string {
  const raw =
    locale === "bn" && pinned.body.bn?.trim()
      ? pinned.body.bn
      : pinned.body.en;
  return stripHtml(raw);
}

function teaserPlain(text: string, maxLen: number): string {
  const tx = text.trim();
  if (tx.length <= maxLen) return tx;
  return `${tx.slice(0, maxLen).trim()}…`;
}

type Props = {
  pinned: PinnedAnnouncementPublic;
  locale: Locale;
};

export function CoursePublicPinnedAnnouncement({ pinned, locale }: Props) {
  const t = useTranslations("courseDetail");
  const [expanded, setExpanded] = useState(false);

  const pinnedPlain = useMemo(() => pinnedBodyPlain(pinned, locale), [pinned, locale]);
  const title =
    getLocalizedField(pinned.title.en, pinned.title.bn, locale) ?? pinned.title.en;

  return (
    <section
      className="relative mb-8 overflow-hidden rounded-xl border border-orange-100 bg-gradient-to-r from-orange-50/90 via-white to-white shadow-[0_2px_16px_rgba(233,77,29,0.06)]"
      aria-label={t("publicPinnedAria")}
    >
      <div className="absolute left-0 top-0 h-full w-1 bg-primary/90" aria-hidden />
      <div className="px-5 py-4 pl-6">
        <div className="min-w-0">
          <p className="text-[13px] font-semibold uppercase tracking-wide text-primary">
            {t("publicPinnedLabel")}
          </p>
          <h2 className="mt-1 text-[16px] font-semibold leading-snug text-neutral-900">{title}</h2>
          <p className="mt-2 text-[14px] leading-relaxed text-neutral-700">
            {expanded ? pinnedPlain : teaserPlain(pinnedPlain, 120)}
          </p>
          {pinnedPlain.length > 120 ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 inline-flex items-center gap-1 text-[13px] font-semibold text-primary hover:underline"
            >
              {expanded ? t("publicPinnedReadLess") : t("publicPinnedReadMore")}
              {expanded ? (
                <ChevronUp aria-hidden className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown aria-hidden className="h-3.5 w-3.5" />
              )}
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}
